import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { FlaskConical, Loader2, ArrowLeft, Mail, Lock, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1 = Email, 2 = Verify OTP, 3 = New Password
  const [loading, setLoading] = useState(false);
  
  // State for forms
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  
  // Array state for 6-digit OTP boxes
  const [otpArray, setOtpArray] = useState(new Array(6).fill(""));
  const inputRefs = useRef([]);
  
  // Security identifiers returned from server
  const [securityHash, setSecurityHash] = useState("");
  const [expiry, setExpiry] = useState("");

  // --- HANDLERS ---
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Please enter your email.");

    try {
      setLoading(true);
      const res = await axios.post("https://api.testoraglobal.in/api/user/forgot-password/send-otp", { email });
      if (res.data.success) {
        toast.success("Verification code sent to your email!");
        setSecurityHash(res.data.hash); 
        setExpiry(res.data.expires);
        setStep(2);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send reset email.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const otpString = otpArray.join("");
    if (otpString.length < 6) return toast.error("Please enter the complete 6-digit code.");

    try {
      setLoading(true);
      const res = await axios.post("https://api.testoraglobal.in/api/user/forgot-password/verify-otp", {
        email,
        otp: otpString,
        hash: securityHash,
        expires: expiry
      });

      if (res.data.success) {
        toast.success("Code verified!");
        setStep(3); // Proceed to password reset step
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Invalid or expired OTP.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("Password must be at least 6 characters.");

    try {
      setLoading(true);
      const res = await axios.post("https://api.testoraglobal.in/api/user/forgot-password/reset", {
        email,
        otp: otpArray.join(""),
        new_password: newPassword,
        hash: securityHash,
        expires: expiry
      });

      if (res.data.success) {
        toast.success("Password updated successfully!");
        navigate("/login");
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  // --- OTP INPUT LOGIC ---
  const handleOtpChange = (element, index) => {
    const value = element.value;
    if (isNaN(value)) return; // Only allow numbers

    const newOtpArray = [...otpArray];
    newOtpArray[index] = value.substring(value.length - 1); // Take only the last entered char
    setOtpArray(newOtpArray);

    // Focus next input automatically
    if (value !== "" && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    // Focus previous input on backspace if current is empty
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").slice(0, 6);
    
    if (/^\d+$/.test(pastedData)) {
      const newOtpArray = [...otpArray];
      pastedData.split("").forEach((char, i) => {
        newOtpArray[i] = char;
      });
      setOtpArray(newOtpArray);
      
      // Auto focus the next empty box or the last box
      const nextIndex = Math.min(pastedData.length, 5);
      inputRefs.current[nextIndex].focus();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#020617] relative overflow-hidden text-white px-4">
      {/* Background Glow */}
      <div className="absolute top-[-200px] left-[-200px] w-[500px] h-[500px] bg-purple-600 rounded-full blur-[150px] opacity-20"></div>
      
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md relative z-10">
        
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 flex items-center justify-center shadow-lg"><FlaskConical size={20} /></div>
            <h1 className="text-3xl font-bold tracking-wide">TESTORA</h1>
          </div>
        </div>

        {/* Card */}
        <div className="bg-[#0B1220]/70 backdrop-blur-xl border border-gray-800 rounded-2xl p-8 shadow-2xl overflow-hidden">
          
          <AnimatePresence mode="wait">
            
            {/* STEP 1: EMAIL */}
            {step === 1 && (
              <motion.div key="step1" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <button onClick={() => navigate("/login")} className="flex items-center gap-2 text-gray-400 hover:text-white transition mb-6 text-sm">
                  <ArrowLeft size={16} /> Back to Login
                </button>
                <h2 className="text-2xl font-semibold mb-2">Reset Password</h2>
                <p className="text-gray-400 text-sm mb-6">Enter your email address and we'll send you a 6-digit verification code.</p>

                <form onSubmit={handleSendOtp}>
                  <div className="mb-6">
                    <label className="block text-sm text-gray-400 mb-2">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" disabled={loading} className="w-full bg-[#111827] border border-gray-700 rounded-lg pl-10 pr-4 py-3 outline-none focus:border-purple-500 transition disabled:opacity-50" />
                    </div>
                  </div>
                  <button type="submit" disabled={loading || !email} className="w-full py-3 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-semibold transition flex items-center justify-center gap-2 disabled:opacity-50">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : "Send Verification Code"}
                  </button>
                </form>
              </motion.div>
            )}

            {/* STEP 2: ENTER OTP */}
            {step === 2 && (
              <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
                <button onClick={() => setStep(1)} disabled={loading} className="flex items-center gap-2 text-gray-400 hover:text-white transition mb-6 text-sm">
                  <ArrowLeft size={16} /> Back
                </button>
                <h2 className="text-2xl font-semibold mb-2">Check your email</h2>
                <p className="text-gray-400 text-sm mb-8">We sent a 6-digit code to <strong className="text-white">{email}</strong></p>

                <form onSubmit={handleVerifyOtp}>
                  <div className="mb-8">
                    <label className="block text-sm text-gray-400 mb-3">Verification Code</label>
                    
                    {/* 6-Box OTP Input */}
                    <div className="flex justify-between gap-2">
                      {otpArray.map((data, index) => (
                        <input
                          key={index}
                          type="text"
                          maxLength="1"
                          ref={(el) => (inputRefs.current[index] = el)}
                          value={data}
                          onChange={(e) => handleOtpChange(e.target, index)}
                          onKeyDown={(e) => handleOtpKeyDown(e, index)}
                          onPaste={handleOtpPaste}
                          disabled={loading}
                          className="w-12 h-14 bg-[#111827] border border-gray-700 rounded-lg text-center text-xl font-bold text-white outline-none focus:border-purple-500 focus:bg-[#1a2235] transition-all disabled:opacity-50"
                        />
                      ))}
                    </div>

                  </div>
                  
                  <button type="submit" disabled={loading || otpArray.join("").length < 6} className="w-full py-3 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-semibold transition flex items-center justify-center gap-2 disabled:opacity-50">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : "Verify Code"}
                  </button>
                </form>
              </motion.div>
            )}

            {/* STEP 3: NEW PASSWORD */}
            {step === 3 && (
              <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <div className="flex items-center gap-2 text-green-400 bg-green-500/10 px-3 py-1.5 rounded-lg w-max mb-6 text-sm font-medium">
                  <CheckCircle2 size={16} /> Code Verified
                </div>
                <h2 className="text-2xl font-semibold mb-2">Create New Password</h2>
                <p className="text-gray-400 text-sm mb-8">Your new password must be at least 6 characters long.</p>

                <form onSubmit={handleResetPassword}>
                  <div className="mb-8">
                    <label className="block text-sm text-gray-400 mb-2">New Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                      <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" disabled={loading} autoFocus className="w-full bg-[#111827] border border-gray-700 rounded-lg pl-10 pr-4 py-3 outline-none focus:border-purple-500 transition disabled:opacity-50" />
                    </div>
                  </div>
                  <button type="submit" disabled={loading || !newPassword} className="w-full py-3 rounded-lg bg-green-600 hover:bg-green-500 text-white font-semibold transition flex items-center justify-center gap-2 disabled:opacity-50">
                    {loading ? <Loader2 size={18} className="animate-spin" /> : "Save New Password"}
                  </button>
                </form>
              </motion.div>
            )}

          </AnimatePresence>

        </div>
      </motion.div>
    </div>
  );
}